import { getChartLabel } from "./chartLabel";
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Form } from "react-bootstrap";
import CustomLegend from "./CustomLegend";
import CustomTooltip from "./CustomTooltip";

const Evths4 = ({ data, filters, selectedState }) => {
  const [showTable, setShowTable] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "place", // default sort by place
    direction: "asc",
  });

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          marginTop: "20px",
        }}
      >
        <h5 style={{ color: "#666", fontSize: "20px" }}>No data available</h5>
      </div>
    );
  }

  const pwtestData = data.map((item) => ({
    place: item.Place,
    PW_tested_hiv: Number(item.PW_tested_screened_for_HIV),
    PW_Screened_reactive: Number(item.PW_Screened_reactive),
    pw_positive: Number(item.PW_Confirmed_HIV_Positive),
    new: Number(item.New_PPW_linked_with_ART),
    known: Number(item.Known_PPW_linked_with_ART),
    total: Number(item.Total_PPW_linked_with_ART),
    new_percentage: Number(item.New_Percentage),
    known_percentage: Number(item.Known_Percentage),
    positivity_rate: Number(item.Burden_Per_Positive_PPW),
  }));

  // Sorting logic
  const sortedData = [...pwtestData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Handle sorting click
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return { key, direction: "asc" };
      }
    });
  };

  // Icon display function
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "⬍"; // default icon
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const chunkArray = (array, chunkCount) => {
    const result = [];
    const chunkSize = Math.ceil(array.length / chunkCount);

    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }

    return result;
  };

  const getChartCount = (length) => {
   if (length < 9) return 1;
    if (length >= 9 && length <= 18) return 2;
    if (length <= 45) return 3;
    return Math.ceil(length / 20);
  };

  const chartCount = getChartCount(pwtestData.length);
  const chunkedData = chunkArray(pwtestData, chartCount);

  return (
    <div style={{ width: "100%", position: "relative" }}>
      {/* Toggle Switch */}
      <div className="card hptCard mt-4 border-0">
        <div class="card-header brBtomlr">
          <div class="headnumText justify-content-between">
            <div class="cardTitle">
              <h5> PW Reactive and Confirmed Positive {getChartLabel(filters, selectedState)}</h5>
            </div>

            <div class="cardDropDown gap-3">
              <div style={{ marginRight: "10px", fontWeight: 500 }}>
                {showTable ? "Chart View" : "Table View"}
              </div>
              <Form>
                <Form.Check
                  type="switch"
                  id="chart-table-toggle"
                  onChange={() => setShowTable((prev) => !prev)}
                  checked={showTable}
                />
              </Form>
            </div>
          </div>
        </div>
      </div>

      {showTable ? (
        // Table View
        <>
          <div className="card hptCard mt-4 border-0">
            <div className="table-responsive">
              <table className="table table-bordered ">
                <thead className="thead-light">
                  <tr>
                    <th className="fw-bold">Place</th>

                    <th
                      className="text-center fw-bold cursor-pointer"
                      onClick={() => handleSort("PW_Screened_reactive")}
                    >
                      PW Screened HIV Reactive
                      {getSortIcon("PW_Screened_reactive")}
                    </th>
                    <th
                      className="text-center fw-bold cursor-pointer"
                      onClick={() => handleSort("pw_positive")}
                    >
                      PW Confirmed HIV Positive
                      {getSortIcon("pw_positive")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item, idx) => (
                    <tr key={idx}>
                      <td className="fw-bold" style={{ fontWeight: "bold" }}>
                        {item.place}
                      </td>

                      <td>{item.PW_Screened_reactive}</td>
                      <td>{item.pw_positive}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card hptCard mt-4 border-0">
            <div className="table-responsive">
              <table className="table table-bordered ">
                <thead className="thead-light">
                  <tr>
                    <th className="fw-bold">Place</th>

                    <th
                      className="text-center fw-bold cursor-pointer"
                      onClick={() => handleSort("new")}
                    >
                      New PPW Linked to ART
                      {getSortIcon("new")}
                    </th>
                    <th
                      className="text-center fw-bold cursor-pointer"
                      onClick={() => handleSort("known")}
                    >
                      Known PPW Linked to ART
                      {getSortIcon("known")}
                    </th>
                    <th
                      className="text-center fw-bold cursor-pointer"
                      onClick={() => handleSort("total")}
                    >
                      Total PPW Linked to ART
                      {getSortIcon("total")}
                    </th>
                    <th
                      className="text-center fw-bold cursor-pointer"
                      onClick={() => handleSort("new_percentage")}
                    >
                      New(%)
                      {getSortIcon("new_percentage")}
                    </th>
                    <th
                      className="text-center fw-bold cursor-pointer"
                      onClick={() => handleSort("known_percentage")}
                    >
                      Known(%)
                      {getSortIcon("known_percentage")}
                    </th>
                    <th
                      className="text-center fw-bold cursor-pointer"
                      onClick={() => handleSort("positivity_rate")}
                    >
                      Positivity Rate
                      {getSortIcon("positivity_rate")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item, idx) => (
                    <tr key={idx}>
                      <td className="fw-bold" style={{ fontWeight: "bold" }}>
                        {item.place}
                      </td>

                      <td>{item.new}</td>
                      <td>{item.known}</td>
                      <td>{item.total}</td>
                      <td>{item.new_percentage}%</td>
                      <td>{item.known_percentage}%</td>
                      <td>{item.positivity_rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        // Chart View
        <>
          {chunkedData.map((chunk, index) => (
            <div key={index} className="card hptCard mt-4 border-0">
              <ResponsiveContainer width="100%" height={600}>
                <BarChart
                  width={500}
                  height={600}
                  data={chunk}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 130,
                  }}
                >
                  <XAxis
                    dataKey="place"
                    angle={-90}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" content={<CustomLegend />} />
                  {/* <Bar
                dataKey="PW_tested_hiv"
                fill="#dc3545"
                name="PW Tested/Screened for HIV"
              >
                <LabelList dataKey="PW_tested_hiv" position="top" />
              </Bar> */}
                  <Bar
                    dataKey="PW_Screened_reactive"
                    fill="#00a084"
                    name="PW Screened HIV Reactive"
                  >
                    <LabelList
                      dataKey="PW_Screened_reactive"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // position slightly above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 could also try "middle" for alignment
                            transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Bar
                    dataKey="pw_positive"
                    fill="#dc3545"
                    name="PW Confirm HIV Positive"
                  >
                    <LabelList
                      dataKey="pw_positive"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // position slightly above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 could also try "middle" for alignment
                            transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
          {chunkedData.map((chunk, index) => (
            <div key={index} className="card hptCard mt-4 border-0">
              <ResponsiveContainer width="100%" height={600}>
                <BarChart
                  width={500}
                  height={600}
                  data={chunk}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 130,
                  }}
                >
                  <XAxis
                    dataKey="place"
                    angle={-90}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" content={<CustomLegend />} />
                  {/* <Bar
                dataKey="PW_tested_hiv"
                fill="#dc3545"
                name="PW Tested/Screened for HIV"
              >
                <LabelList dataKey="PW_tested_hiv" position="top" />
              </Bar> */}
                  <Bar
                    dataKey="known"
                    fill="#dc3545"
                    name="Already Known HIV positive Pregnant Women on ART"
                  >
                    <LabelList
                      dataKey="known"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // position slightly above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 could also try "middle" for alignment
                            transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Bar
                    dataKey="new"
                    fill="#00a084"
                    name="Newly detected HIV positive Pregnant Women linked to ART"
                  >
                    <LabelList
                      dataKey="new"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // position slightly above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 could also try "middle" for alignment
                            transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default Evths4;
